import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import logo from '../../shared/assets/logos/ARBCOR_Text_only.png';
import logoMark from '../../shared/assets/logos/BlackLogoPNG_TransparentBackground.png';

const metrics = [
  { label: 'Revenue', value: '$1.24M', change: '+12.4%', up: true },
  { label: 'Orders', value: '2,847', change: '+8.1%', up: true },
  { label: 'Avg Ticket', value: '$42.30', change: '+2.3%', up: true },
  { label: 'Labor Cost', value: '28.1%', change: '-3.1%', up: false },
  { label: 'Inventory Turns', value: '8.2x', change: '+1.4%', up: true },
  { label: 'Retention', value: '94.2%', change: '+0.8%', up: true },
  { label: 'Margin', value: '38.7%', change: '+4.2%', up: true },
  { label: 'Fulfillment', value: '99.1%', change: '+0.3%', up: true },
  { label: 'Active Users', value: '1,204', change: '+15.7%', up: true },
  { label: 'CSAT Score', value: '4.8', change: '+0.2', up: true },
];

const ComingSoon = () => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen bg-white flex items-center relative overflow-hidden">
      {/* Admin Login — top right */}
      <Link
        to="/preview"
        className={`absolute top-6 right-6 z-20 px-4 py-2 border border-zinc-200 text-zinc-400 text-xs tracking-wider uppercase hover:border-zinc-400 hover:text-zinc-600 transition-all duration-1000 ${
          loaded ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ transitionDelay: '1500ms' }}
      >
        Admin Login
      </Link>

      {/* Logo mark background */}
      <div
        className={`absolute right-[-10%] md:right-[-5%] top-1/2 -translate-y-1/2 transition-all duration-[1500ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${
          loaded ? 'opacity-[0.035] scale-100' : 'opacity-0 scale-[0.95]'
        }`}
        style={{ transitionDelay: '300ms' }}
      >
        <img
          src={logoMark}
          alt=""
          className="w-[28rem] md:w-[36rem] lg:w-[44rem] h-auto select-none pointer-events-none"
          draggable="false"
        />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full">
        <div className="max-w-2xl flex flex-col items-center lg:items-start text-center lg:text-left">
          {/* Logo */}
          <div
            className={`mb-12 transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: '200ms' }}
          >
            <img
              src={logo}
              alt="Arbcor"
              className="h-6 md:h-8 w-auto select-none pointer-events-none"
              draggable="false"
              onContextMenu={(e) => e.preventDefault()}
            />
          </div>

          {/* Headline */}
          <h1 className="mb-8">
            <span
              className={`block font-light text-zinc-900 leading-[1.1] transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)', transitionDelay: '500ms' }}
            >
              Operational Intelligence
            </span>
            <span
              className={`block font-extralight text-zinc-300 leading-[1.1] transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)', transitionDelay: '700ms' }}
            >
              for Everyone.
            </span>
          </h1>

          {/* Animated divider */}
          <div
            className="h-px bg-zinc-200 mb-8 transition-all duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
            style={{ width: loaded ? '120px' : '0px', transitionDelay: '900ms' }}
          />

          {/* Tagline with shine */}
          <p
            className={`text-lg md:text-xl font-light leading-relaxed mb-12 transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: '1100ms' }}
          >
            <span className="text-shine">Something smarter is coming.</span>
          </p>

          {/* CTA */}
          <div
            className={`transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: '1300ms' }}
          >
            <a
              href="mailto:contact@arbcor.com"
              className="group inline-flex items-center gap-3 text-sm tracking-wider text-zinc-900 uppercase"
            >
              Get in Touch
              <span className="inline-block w-8 h-px bg-zinc-900 group-hover:w-14 transition-all duration-500" />
            </a>
            <p className="text-zinc-400 text-xs mt-4 tracking-wide">contact@arbcor.com</p>
          </div>
        </div>
      </div>

      {/* Metric Ticker */}
      <div
        className={`absolute bottom-24 left-0 right-0 overflow-hidden transition-all duration-1000 ${
          loaded ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ transitionDelay: '1600ms' }}
      >
        <div className="flex animate-[tickerScroll_30s_linear_infinite] w-max">
          {[...metrics, ...metrics].map((m, i) => (
            <div key={i} className="flex items-center gap-3 px-6 border-r border-zinc-100">
              <span className="text-[10px] uppercase tracking-wider text-zinc-400">{m.label}</span>
              <span className="text-sm font-medium text-zinc-900 tabular-nums">{m.value}</span>
              <span className={`text-[10px] tabular-nums ${m.up ? 'text-zinc-500' : 'text-zinc-400'}`}>
                {m.up ? '▲' : '▼'} {m.change}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Copyright footer */}
      <div
        className={`absolute bottom-8 left-0 right-0 text-center lg:text-left lg:left-6 lg:right-auto text-zinc-400 text-xs tracking-wider transition-all duration-1000 ${
          loaded ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ transitionDelay: '1500ms' }}
      >
        &copy; {new Date().getFullYear()} Arbcor Systems
      </div>
    </div>
  );
};

export default ComingSoon;
